import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { StudyTypeIconDescriptor } from "src/pages/Studies/transformer";
import { ITag } from "src/components/Searchbar/interface";
import {
	accessErrorTypes,
	fileTypes,
	ICollaboratorData,
	IDropdownMaster,
	IIterationPayload,
	IMetaDetails,
	INotesPreviewData,
	INotesTemplate,
	ISelectedStudy,
	IStudyData,
	notesType,
	StudyPageTabType,
	StudyPageViewType,
	SupportedLanguageType,
} from "src/pages/Studies/models/interfaces";

interface StudiesState {
	studiesData: IStudyData[];
	collaboratorData: ICollaboratorData[];
	dropdownMaster: IDropdownMaster[];
	searchText: string;
	selectedCollaborators: ITag[];
	metaDetails: IMetaDetails;
	userProfile: string;
	role: string;
	selectedStudy: ISelectedStudy;
	allStudyTypes: { label: string; value: string; icon: StudyTypeIconDescriptor | null }[];
	selectedStudyType: { label: string; value: string; icon: StudyTypeIconDescriptor | null };
	allNotesTemplates: { id: string; name: notesType; template: INotesTemplate[] }[];
	selectedNotesType: { id: string; name: notesType; template: INotesTemplate[] };
	notesPreviewData: INotesPreviewData[];
	uploadedNotesId: string;
	saveColumnMappingPayload: {
		study_id: string;
		note_id: string;
		remapped_cols?: string[];
		column_remap?: boolean;
		file_id: string;
		column_mapping: {
			field_category: string;
			input_field_name: string;
			is_datetime: boolean;
			date_format?: string;
		}[];
		start_date?: string;
		end_date?: string;
	};
	uploadedNotesFiles: { name: string; id: string; note_id: string; dateRange: [string, string] }[];
	isStudyTypeSelectionScreenVisible: boolean;
	supportedLanguages: string[];
	isForbiddenModalOpen: boolean;
	isForbiddenMessage: string;
	accessErrorType: string;
	isNavDisabled: boolean;
	selectedStudyPageView: StudyPageViewType;
	selectedStudyPageTab: StudyPageTabType;
	addIteration: IIterationPayload[];
	isConfigDisabled: boolean;
	selectedCard: boolean;
	selectedCheckBox: any;
	allStoryBoard: any;
}

export const initialState: StudiesState = {
	studiesData: [],
	collaboratorData: [],
	dropdownMaster: [],
	searchText: "",
	selectedCollaborators: [],
	metaDetails: {
		config: {
			is_report_available: {
				home: true,
				manage_study: false,
				add_study: true,
				ask_kural: true,
			},
		},
		id: "",
		fullName: "",
		user_email: "",
		isAdmin: false,
		roles: [],
		profiles: [],
		current_profile_id: "",
		current_primary_language: "",
		current_secondary_language: [],
		profile_language_mapping: [],
		feature_mapping: [],
	},

	userProfile: "",
	role: "",
	selectedStudy: {
		study_id: null,
		study_name: null,
		study_tags: [],
		study_collaborators: [],
		insights_id: null,
		access_type: null,
		created_by: null,
		study_type: null,
		insights_available: null,
	},
	allStudyTypes: [],
	selectedStudyType: null,
	allNotesTemplates: [],
	selectedNotesType: null,
	uploadedNotesId: null,
	notesPreviewData: [],
	uploadedNotesFiles: [],
	saveColumnMappingPayload: null,
	isStudyTypeSelectionScreenVisible: true,
	supportedLanguages: [SupportedLanguageType.English],
	isForbiddenModalOpen: false,
	isForbiddenMessage: "",
	accessErrorType: "",
	isNavDisabled: false,
	selectedStudyPageView: StudyPageViewType.Grid,
	selectedStudyPageTab: StudyPageTabType.Recent,
	addIteration: [],
	isConfigDisabled: true,
	selectedCard: true,
	selectedCheckBox: [],
	allStoryBoard: [],
};

const storyBoardSlice = createSlice({
	name: "story-board",
	initialState,
	reducers: {
		setAllStudiesData(state, action: PayloadAction<IStudyData[]>) {
			state.studiesData = action.payload;
		},
		setAllCollaboratorsData(state, action: PayloadAction<ICollaboratorData[]>) {
			state.collaboratorData = action.payload;
		},
		setDropdownMaster(state, action: PayloadAction<IDropdownMaster[]>) {
			state.dropdownMaster = action.payload;
		},
		setSearchText(state, action: PayloadAction<string>) {
			state.searchText = action.payload;
		},
		setSelectedCollaborators(state, action: PayloadAction<ITag[]>) {
			state.selectedCollaborators = action.payload;
		},
		updateStudyCollaboratorData(state, action: PayloadAction<{ collab_details: any; study_id: string }>) {
			const { study_id, collab_details } = action.payload;
			const study = state.studiesData.find((s) => s.study_id === study_id);
			study.collaborators = collab_details;
		},
		updateFileCount: (state, action: PayloadAction<{ study_id: string; study_type: string; count: number }>) => {
			const { study_id, study_type, count } = action.payload;
			const study = state.studiesData?.find((s) => s.study_id === study_id);
			if (study_type === fileTypes.transcript) study.count_of_file = count;
			if (study_type === fileTypes.report) study.count_of_file = count;
			if (study_type === fileTypes.notes) study.count_of_file = count;
		},
		setMetaDetails: (state, action: PayloadAction<IMetaDetails>) => {
			state.metaDetails = {
				...state.metaDetails,
				...action.payload,
				config: action.payload?.config ?? state.metaDetails.config,
			};
		},
		setUserProfile: (state, action: PayloadAction<string>) => {
			state.userProfile = action.payload;
		},
		setRole: (state, action: PayloadAction<string>) => {
			state.role = action.payload;
		},
		setSelectedStudy(state, action: PayloadAction<ISelectedStudy>) {
			state.selectedStudy = action.payload;
		},
		updateSelectedStudy(state, action: PayloadAction<any>) {
			state.selectedStudy = {
				...state.selectedStudy,
				...action.payload,
			};
		},
		setAllStudyTypes(state, action: PayloadAction<{ label: string; value: string; icon: StudyTypeIconDescriptor | null }[]>) {
			state.allStudyTypes = action.payload;
		},
		setSelectedStudyType(state, action: PayloadAction<{ label: string; value: string; icon: StudyTypeIconDescriptor | null }>) {
			state.selectedStudyType = action.payload;
		},
		setAllNotesTemplates(state, action: PayloadAction<{ name: notesType; id: string; template: INotesTemplate[] }[]>) {
			state.allNotesTemplates = action.payload;
		},
		setSelectedNotesType(state, action: PayloadAction<{ id: string; name: notesType; template: INotesTemplate[] }>) {
			state.selectedNotesType = action.payload;
		},
		setNotesPreviewData(state, action: PayloadAction<INotesPreviewData[]>) {
			state.notesPreviewData = action.payload;
		},
		setUploadedNotesId(state, action: PayloadAction<string>) {
			state.uploadedNotesId = action.payload;
		},
		setUploadedNotesFiles(state, action: PayloadAction<{ name: string; id: string; note_id: string; dateRange: [string, string] }[]>) {
			state.uploadedNotesFiles = action.payload;
		},
		updateSaveColumnMappingPayload(state, action: PayloadAction<Partial<typeof state.saveColumnMappingPayload>>) {
			state.saveColumnMappingPayload = {
				...state.saveColumnMappingPayload,
				...action.payload,
			};
		},
		setIsStudyTypeSelectionScreenVisible(state, action: PayloadAction<boolean>) {
			state.isStudyTypeSelectionScreenVisible = action.payload;
		},
		setSupportedLanguages(state, action: PayloadAction<string[]>) {
			state.supportedLanguages = action.payload;
		},
		openForbiddenModal: (state, action: PayloadAction<{ message: string; type: string }>) => {
			const { message, type } = action.payload;
			state.isForbiddenModalOpen = true;
			state.isForbiddenMessage = message;
			state.accessErrorType = type;
			state.isNavDisabled =
				type === accessErrorTypes.NO_ROLES ||
				type === accessErrorTypes.NO_PROFILES ||
				type === accessErrorTypes.ADMIN_ROLE_NO_PROFILE ||
				type === accessErrorTypes.PROFILE_REVOKED ||
				type === accessErrorTypes.ADMIN_REVOKED ||
				type === accessErrorTypes.ACCOUNT_DISABLED ||
				type === accessErrorTypes.ACCESS_EXPIRED;
		},
		closeForbiddenModal: (state) => {
			state.isForbiddenModalOpen = false;
		},
		setIsNavDisabled: (state, action: PayloadAction<boolean>) => {
			state.isNavDisabled = action.payload;
		},
		setSelectedStudyPageView(state, action: PayloadAction<StudyPageViewType>) {
			state.selectedStudyPageView = action.payload;
		},
		setSelectedStudyPageTab(state, action: PayloadAction<StudyPageTabType>) {
			state.selectedStudyPageTab = action.payload;
		},
		setAddIteration(state, action: PayloadAction<IIterationPayload[]>) {
			state.addIteration = action.payload;
		},
		updateStudyStar(state, action: PayloadAction<{ study_id: string; star: boolean }>) {
			const { study_id, star } = action.payload;
			const study = state.studiesData.find((s) => s.study_id === study_id);
			if (study) {
				study?.collaborators?.forEach((collab) => {
					if (collab?.collaborator_id === state.metaDetails.id) {
						collab.is_star = star;
					}
				});
			}
		},
		setIsConfigDisabled(state, action: PayloadAction<boolean>) {
			state.isConfigDisabled = action.payload;
		},
		setSelectedCard(state, action: PayloadAction<boolean>) {
			state.selectedCard = action.payload;
		},
		setAllStoryBoard(state, action: PayloadAction<any>) {
			state.allStoryBoard = action.payload;
		},
		setSelectedCheckBox(state, action: PayloadAction<any>) {
			state.selectedCheckBox = action.payload;
		},
		// You can add more reducers here if needed
	},
});

export const {
	setAllCollaboratorsData,
	setAllStudiesData,
	setDropdownMaster,
	setMetaDetails,
	setSearchText,
	setSelectedCollaborators,
	setSelectedStudy,
	setUserProfile,
	updateFileCount,
	updateSelectedStudy,
	setAllStudyTypes,
	setSelectedStudyType,
	setAllNotesTemplates,
	setSelectedNotesType,
	setNotesPreviewData,
	setUploadedNotesId,
	setUploadedNotesFiles,
	updateSaveColumnMappingPayload,
	setIsStudyTypeSelectionScreenVisible,
	updateStudyCollaboratorData,
	setSupportedLanguages,
	setRole,
	openForbiddenModal,
	closeForbiddenModal,
	setIsNavDisabled,
	setSelectedStudyPageView,
	setSelectedStudyPageTab,
	setAddIteration,
	updateStudyStar,
	setIsConfigDisabled,
	setSelectedCard,
	setAllStoryBoard,
	setSelectedCheckBox,
} = storyBoardSlice.actions;

export default storyBoardSlice.reducer;
